/**
 * dates.ts — pure `YYYY-MM-DD` / `YYYY-MM` arithmetic and the period labels of the period
 * picker (spec-business §3.4).
 *
 * Days are compared lexicographically and never parsed with `new Date(dateOnlyString)` (that
 * parses as UTC and shifts a day in Asia/Jerusalem). Everything here is a pure function — the
 * client never derives "today": `asOf` always comes from the API (`SummaryResponse.asOf`).
 */

import type { DayString, MonthString, Period } from '../../api-types.ts';
import { fmtDate, monthName } from './format.ts';
import { he } from './i18n/he.ts';

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** `2026-09-06` → `[2026, 9, 6]`. */
export function parts(day: string): [number, number, number] {
  const p = day.slice(0, 10).split('-');
  return [Number(p[0]), Number(p[1]), Number(p[2])];
}

export function iso(y: number, m: number, d: number): DayString {
  return `${y}-${pad(m)}-${pad(d)}`;
}

/** Days in a calendar month (1-based month). */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function addDays(day: DayString, n: number): DayString {
  const [y, m, d] = parts(day);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return iso(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

export function addMonths(month: MonthString, n: number): MonthString {
  const p = month.slice(0, 7).split('-');
  const t = new Date(Date.UTC(Number(p[0]), Number(p[1]) - 1 + n, 1));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}`;
}

export function daysBetween(from: DayString, to: DayString): number {
  const [ay, am, ad] = parts(from);
  const [by, bm, bd] = parts(to);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
}

/** 0 = Sunday … 6 = Saturday. */
export function weekday(day: DayString): number {
  const [y, m, d] = parts(day);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export function monthOf(day: DayString): MonthString {
  return day.slice(0, 7);
}

export function monthStart(month: MonthString): DayString {
  return `${month.slice(0, 7)}-01`;
}

export function monthEnd(month: MonthString): DayString {
  const p = month.slice(0, 7).split('-');
  const y = Number(p[0]);
  const m = Number(p[1]);
  return iso(y, m, daysInMonth(y, m));
}

/** Every calendar month touched by `[from, to]`, ascending. */
export function monthsBetween(from: DayString, to: DayString): MonthString[] {
  const out: MonthString[] = [];
  let m = monthOf(from);
  const last = monthOf(to);
  while (m <= last) {
    out.push(m);
    m = addMonths(m, 1);
  }
  return out;
}

/** The last `count` months ending at `month`, ascending, never before `historyFrom`. */
export function trailingMonths(month: MonthString, count: number, historyFrom?: MonthString): MonthString[] {
  const out: MonthString[] = [];
  for (let i = count - 1; i >= 0; i--) {
    const m = addMonths(month, -i);
    if (historyFrom && m < historyFrom) continue;
    out.push(m);
  }
  return out;
}

/**
 * The period-picker chip: "ספטמבר 2026 (עד היום)" for the running month, the plain month name
 * for a closed one, and the range for a custom window (spec §3.4).
 */
export function periodChipLabel(p: Period): string {
  switch (p.key) {
    case 'month':
      return p.partial ? he.period.currentMonth(monthName(p.lastMonth)) : monthName(p.lastMonth);
    case 'prev':
      return monthName(p.lastMonth);
    case '3m':
      return he.period.m3;
    case '12m':
      return he.period.m12;
    default:
      return `${fmtDate(p.from)} – ${fmtDate(p.to)}`;
  }
}

/** The long label used in headings ("ספטמבר 2026 · 1–6.9"). */
export function periodLabel(p: Period): string {
  if (p.key === 'custom') return `${fmtDate(p.from)} – ${fmtDate(p.to)}`;
  const first = p.months[0] ?? p.lastMonth;
  const last = p.months[p.months.length - 1] ?? p.lastMonth;
  if (first === last) {
    if (!p.partial) return monthName(last);
    const [, , d] = parts(p.to);
    const [, m] = parts(p.to);
    return `${monthName(last)} · 1–${d}.${m}`;
  }
  const sameYear = first.slice(0, 4) === last.slice(0, 4);
  return `${monthName(first, !sameYear)}–${monthName(last)}`;
}

/**
 * The comparison label of the "החודש" card: "לעומת 1–6.8" for a running month, "לעומת אוגוסט
 * 2026" for a closed one (spec §2.2 "השוואה לחודש קודם"). `null` when there is nothing to
 * compare against (the first month of history).
 */
export function prevPeriodLabel(p: Period): string | null {
  if (!p.prev) return null;
  const { from, to } = p.prev;
  const fromMonth = monthOf(from);
  const toMonth = monthOf(to);
  if (fromMonth !== toMonth) return `${he.period.comparedTo} ${fmtDate(from)} – ${fmtDate(to)}`;
  const [, , d1] = parts(from);
  const [y, m, d2] = parts(to);
  if (d1 === 1 && d2 === daysInMonth(y, m)) return `${he.period.comparedTo} ${monthName(fromMonth)}`;
  return `${he.period.comparedTo} ${d1}–${d2}.${m}`;
}

/** Month progress for the thin bar under "החודש" (spec §4.1 A2). */
export function monthProgressRatio(day: number, daysTotal: number): number {
  if (!daysTotal) return 0;
  return Math.min(1, Math.max(0, day / daysTotal));
}

/** The `YYYY-MM` options of the period menu, newest first (spec §3.4: 12 months back). */
export function periodMenuMonths(asOf: DayString, count = 13): MonthString[] {
  const out: MonthString[] = [];
  const current = monthOf(asOf);
  for (let i = 0; i < count; i++) out.push(addMonths(current, -i));
  return out;
}
